import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  UnorderedList,
  ListItem
} from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const Disclaimer: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/disclaimer"
        fallbackTitle="Disclaimer"
        fallbackDescription="Important disclaimers and legal information"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg="orange.600" color="white" py={14}>
        <Container maxW="container.xl">
          <VStack spacing={4} align="start">
            <Text fontSize="xs" color="orange.200" textTransform="uppercase" letterSpacing="widest">
              Important Legal Notice
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Disclaimer
            </Heading>
            <Text fontSize="lg" color="orange.100">
              Please read this disclaimer carefully before using our services
            </Text>
            <Text fontSize="sm" color="orange.200" mt={2}>
              Last Updated: November 2025
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.lg" py={16}>
        <VStack spacing={8} align="stretch">

          {/* Important Notice Alert */}
          <Alert status="warning" variant="left-accent" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Important Notice</AlertTitle>
              <AlertDescription fontSize="sm">
                <strong>[PLACEHOLDER]</strong> The information provided on this platform is for general informational
                purposes only. It should not be relied upon as professional advice. Always seek independent professional
                advice before making important decisions.
              </AlertDescription>
            </Box>
          </Alert>

          {/* General Disclaimer */}
          <Box>
            <Heading size="lg" mb={4} color="gray.800">General Disclaimer</Heading>
            <Text color="gray.700" lineHeight="tall" mb={4}>
              <strong>[PLACEHOLDER CONTENT]</strong> One Group Pty Ltd (ABN XX XXX XXX XXX) ("we", "us", "our")
              provides this platform and its content on an "as is" basis. While we strive to ensure the accuracy and
              reliability of information provided, we make no representations or warranties of any kind, express or
              implied, about the completeness, accuracy, reliability, suitability, or availability of the platform or
              the information contained herein.
            </Text>
            <Text color="gray.700" lineHeight="tall">
              Your use of this platform and reliance on any information is solely at your own risk. We shall not be
              liable for any loss or damage, including without limitation indirect or consequential loss or damage,
              arising from the use of this platform or its content.
            </Text>
          </Box>

          <Divider />

          {/* Financial and Investment Disclaimer */}
          <Box>
            <Heading size="lg" mb={4} color="gray.800">Financial and Investment Disclaimer</Heading>
            <Alert status="info" variant="solid" bg="orange.500" borderRadius="md" mb={4}>
              <AlertIcon />
              <AlertDescription fontSize="sm">
                <strong>Not Financial Advice:</strong> Nothing on this platform constitutes financial, investment,
                legal, or tax advice.
              </AlertDescription>
            </Alert>
            <VStack align="start" spacing={3}>
              <Text color="gray.700" lineHeight="tall">
                <strong>[PLACEHOLDER]</strong> The information provided through our platform, including but not limited
                to pricing data, market information, and business insights, is for general informational purposes only
                and should not be construed as:
              </Text>
              <UnorderedList spacing={2} color="gray.700" pl={6}>
                <ListItem>Financial product advice or recommendations</ListItem>
                <ListItem>Investment advice or solicitation to buy or sell securities</ListItem>
                <ListItem>Tax advice or tax planning strategies</ListItem>
                <ListItem>Legal advice or interpretation of laws and regulations</ListItem>
                <ListItem>Professional advice specific to your circumstances</ListItem>
              </UnorderedList>
              <Text color="gray.700" lineHeight="tall" fontSize="sm" fontStyle="italic">
                You should consult with qualified financial advisors, accountants, lawyers, or other professionals
                before making any financial or business decisions based on information from this platform.
              </Text>
            </VStack>
          </Box>

          <Divider />

          {/* Property and Real Estate Disclaimer */}
          <Box>
            <Heading size="lg" mb={4} color="gray.800">Property and Real Estate Information</Heading>
            <Text color="gray.700" lineHeight="tall" mb={4}>
              <strong>[PLACEHOLDER]</strong> Any property-related information, including but not limited to property
              valuations, market data, rental yields, or investment potential, is provided for general informational
              purposes only. This information:
            </Text>
            <UnorderedList spacing={2} color="gray.700" pl={6} mb={4}>
              <ListItem>Does not constitute a formal property valuation or appraisal</ListItem>
              <ListItem>Should not be relied upon for purchase, sale, or investment decisions</ListItem>
              <ListItem>May not reflect current market conditions or property-specific factors</ListItem>
              <ListItem>Is subject to change without notice</ListItem>
              <ListItem>May contain errors or omissions</ListItem>
            </UnorderedList>
            <Text color="gray.700" lineHeight="tall" fontSize="sm">
              Always obtain independent professional property valuations and legal advice before entering into any
              property transactions.
            </Text>
          </Box>

          <Divider />

          {/* No Warranties */}
          <Box>
            <Heading size="lg" mb={4} color="gray.800">No Warranties</Heading>
            <Text color="gray.700" lineHeight="tall" mb={4}>
              <strong>[PLACEHOLDER]</strong> We do not warrant that:
            </Text>
            <UnorderedList spacing={2} color="gray.700" pl={6}>
              <ListItem>The platform will be available at all times or uninterrupted</ListItem>
              <ListItem>The platform will be free from errors, viruses, or other harmful components</ListItem>
              <ListItem>The information provided is accurate, complete, or up-to-date</ListItem>
              <ListItem>The use of the platform will meet your specific requirements</ListItem>
              <ListItem>Any defects in the platform will be corrected</ListItem>
              <ListItem>Results obtained from using the platform will be reliable</ListItem>
            </UnorderedList>
          </Box>

          <Divider />

          {/* Limitation of Liability */}
          <Box>
            <Heading size="lg" mb={4} color="gray.800">Limitation of Liability</Heading>
            <Alert status="warning" variant="left-accent" borderRadius="md" mb={4}>
              <AlertIcon />
              <AlertDescription fontSize="sm">
                <strong>Liability Cap:</strong> Our liability is limited to the maximum extent permitted by law.
              </AlertDescription>
            </Alert>
            <Text color="gray.700" lineHeight="tall" mb={4}>
              <strong>[PLACEHOLDER]</strong> To the fullest extent permitted by law, One Group Pty Ltd, its directors,
              employees, partners, agents, and affiliates shall not be liable for:
            </Text>
            <UnorderedList spacing={2} color="gray.700" pl={6} mb={4}>
              <ListItem>Any direct, indirect, incidental, special, or consequential damages</ListItem>
              <ListItem>Loss of profits, revenue, data, or business opportunities</ListItem>
              <ListItem>Damages arising from your use or inability to use the platform</ListItem>
              <ListItem>Reliance on any information provided through the platform</ListItem>
              <ListItem>Unauthorized access to or alteration of your data</ListItem>
              <ListItem>Actions or omissions of third parties</ListItem>
            </UnorderedList>
            <Text color="gray.700" lineHeight="tall" fontSize="sm" fontStyle="italic">
              Nothing in this disclaimer excludes or limits liability that cannot be excluded or limited under the
              Australian Consumer Law or other applicable laws.
            </Text>
          </Box>

          <Divider />

          {/* External Links */}
          <Box>
            <Heading size="lg" mb={4} color="gray.800">External Links and Third-Party Content</Heading>
            <Text color="gray.700" lineHeight="tall" mb={4}>
              <strong>[PLACEHOLDER]</strong> Our platform may contain links to external websites or third-party
              content. These links are provided for your convenience only. We:
            </Text>
            <UnorderedList spacing={2} color="gray.700" pl={6} mb={4}>
              <ListItem>Do not endorse or take responsibility for the content of external sites</ListItem>
              <ListItem>Are not responsible for the accuracy or reliability of third-party content</ListItem>
              <ListItem>Do not guarantee the availability of external links</ListItem>
              <ListItem>Are not liable for any damages arising from your use of external websites</ListItem>
              <ListItem>Do not control the privacy practices of third-party websites</ListItem>
            </UnorderedList>
            <Text color="gray.700" lineHeight="tall" fontSize="sm">
              You access external websites and use third-party content at your own risk. We recommend reviewing the
              terms and privacy policies of any external sites you visit.
            </Text>
          </Box>

          <Divider />

          {/* Changes to Disclaimer */}
          <Box>
            <Heading size="lg" mb={4} color="gray.800">Changes to This Disclaimer</Heading>
            <Text color="gray.700" lineHeight="tall">
              <strong>[PLACEHOLDER]</strong> We reserve the right to modify this disclaimer at any time without prior
              notice. Changes will be effective immediately upon posting to this page. Your continued use of the
              platform after any changes constitutes acceptance of the updated disclaimer. We recommend reviewing this
              page periodically to stay informed of any updates.
            </Text>
          </Box>

          {/* Australian Consumer Law Notice */}
          <Alert status="info" variant="left-accent" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="md">Australian Consumer Law Notice</AlertTitle>
              <AlertDescription fontSize="sm">
                <strong>[PLACEHOLDER]</strong> Nothing in this disclaimer is intended to exclude, restrict, or modify
                any consumer rights under the Australian Consumer Law (Schedule 2 of the Competition and Consumer Act
                2010) or any other applicable law that cannot be excluded, restricted, or modified by agreement. To the
                extent any warranty or guarantee cannot be excluded, our liability is limited to the resupply of
                services or payment of the cost of resupply, at our discretion.
              </AlertDescription>
            </Box>
          </Alert>

          {/* Contact Information */}
          <Box bg="orange.50" p={6} borderRadius="md" borderLeft="4px solid" borderColor="orange.500">
            <Heading size="sm" mb={3} color="orange.900">Questions About This Disclaimer?</Heading>
            <Text fontSize="sm" color="gray.700" mb={3}>
              <strong>[PLACEHOLDER]</strong> If you have any questions or concerns about this disclaimer, please
              contact us:
            </Text>
            <VStack align="start" spacing={1} fontSize="sm" color="gray.700">
              <Text><strong>Email:</strong> legal@onegroup.com.au</Text>
              <Text><strong>Phone:</strong> 1300 XXX XXX</Text>
              <Text><strong>Address:</strong> [Placeholder Address], Melbourne VIC 3000</Text>
            </VStack>
          </Box>

        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Disclaimer;
